import { AutocompleteGenericEntity, DomainCode } from '@australiangreens/ag-internal-components';
import { Search as BranchesPopupIcon } from '@mui/icons-material';
import { Box, FormControl, Grid, InputLabel, MenuItem, Tooltip } from '@mui/material';
import {
  BaseWidget,
  BaseWidgetProps,
  Config,
  FactoryWithContext,
} from '@react-awesome-query-builder/mui';
import { useContext } from 'react';

import { DOMAINS_WITH_IDS } from '@/constants/domains';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import { MappingError } from '@/pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/FilterMappers';
import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { NotRenewingType } from '@/types/shared';
import MissingFilterValueWarning from './MissingFilterValueWarning';
import TemplateCapableFetchAutocomplete from './templateCapableComponents/TemplateCapableFetchAutocomplete';
import TemplateCapableSelect from './templateCapableComponents/TemplateCapableSelectV2';

const MEMBERSHIP_STATUSES_WITH_IDS: AutocompleteGenericEntity[] = [
  {
    id: 1,
    label: 'New',
  },
  {
    id: 7,
    label: 'Provisional',
  },
  {
    id: 2,
    label: 'Current',
  },
  {
    id: 3,
    label: 'Grace',
  },
  {
    id: 4,
    label: 'Expired',
  },
  {
    id: 12,
    label: 'Moved Interstate',
  },
  {
    id: 5,
    label: 'Pending',
  },
  {
    id: 6,
    label: 'Cancelled',
  },

  {
    id: 8,
    label: 'Deceased',
  },
  {
    id: 10,
    label: 'Resigned',
  },
  {
    id: 11,
    label: 'Unfinancial',
  },
  {
    id: 13,
    label: 'Provisional - incomplete application',
  },
  {
    id: 14,
    label: 'Membership suspended',
  },
  {
    id: 16,
    label: 'Member expelled',
  },
  {
    id: 15,
    label: 'Application rejected',
  },
  {
    id: 17,
    label: 'Application withdrawn',
  },
];

const pickDomains = (domainCode: DomainCode) => {
  if (domainCode !== undefined) {
    const domain = DOMAINS_WITH_IDS.find(({ id }) => id === domainCode);
    if (domain !== undefined) return [domain];
  }
  return [];
};

type MembershipWidgetValue = {
  domains: AutocompleteGenericEntity[];
  statuses: AutocompleteGenericEntity[] | TemplatePlaceholder;
  branches: AutocompleteGenericEntity[] | TemplatePlaceholder;
  notRenewing: NotRenewingType | TemplatePlaceholder;
  errors: MappingError[];
};

type MembershipWidgetProps = {
  lookupBranches: (lookupValue: string) => Promise<AutocompleteGenericEntity[]>;

  domainCode: DomainCode;
};

type CombinedProps = BaseWidgetProps<Config, MembershipWidgetValue> & MembershipWidgetProps;

/** Currently the user cannot select the domain/state to search in, it is
 * provided from their current domain/org selection */
const MembershipWidgetFactory: FactoryWithContext<CombinedProps> = ({
  value,
  setValue,
  lookupBranches,
  domainCode,
}: CombinedProps) => {
  const inSearchTemplate = useContext(SearchTemplatePageContext);
  const isNotRenewingPlaceHolder = isTemplatePlaceholder(value?.notRenewing);

  const handleChangeStatuses = (newValue: AutocompleteGenericEntity[] | TemplatePlaceholder) => {
    // setValue does not support updater functions like useState hook setter
    // function does, so we have to reference the old value directly.
    const statusesCleared = Array.isArray(newValue) && newValue.length === 0;
    setValue({
      ...(value ?? { domains: pickDomains(domainCode) }),
      statuses: newValue,
      branches: statusesCleared
        ? isTemplatePlaceholder(value?.branches)
          ? value.branches
          : []
        : (value?.branches ?? []),
      notRenewing: statusesCleared
        ? isTemplatePlaceholder(value?.notRenewing)
          ? value.notRenewing
          : ''
        : (value?.notRenewing ?? ''),
      errors: [],
    });
  };

  const handleChangeBranches = (newValue: AutocompleteGenericEntity[] | TemplatePlaceholder) => {
    setValue({
      ...(value ?? { domains: pickDomains(domainCode) }),
      statuses: value?.statuses ?? [],
      branches: newValue,
      notRenewing: value?.notRenewing ?? '',
      errors: [],
    });
  };

  const handleChangeNotRenewing = (selectValue: TemplatePlaceholder | string) => {
    setValue({
      ...(value ?? { domains: pickDomains(domainCode) }),
      statuses: value?.statuses ?? [],
      notRenewing: selectValue,
      errors: [],
    } as MembershipWidgetValue);
  };

  const showBranchesAutocomplete =
    isTemplatePlaceholder(value?.statuses) || (value?.statuses ?? []).length !== 0;

  // When the Not Renewing control is showing, the alignItems="center" of the
  // <Box> component under showBranchesAutocomplete will move the
  // <MissingFilterValueWarning down; The missingFilterValueWarningStyle
  // const is there to correct things back.

  const missingFilterValueWarningStyle = { marginTop: '-81px' };

  const listValuesForDisplay = [
    { title: '\u00a0', value: '' },
    { title: 'Exclude', value: 'exclude' },
    { title: 'Only include', value: 'onlyInclude' },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '1rem',
        flexWrap: 'wrap',
      }}
    >
      <TemplateCapableFetchAutocomplete
        minLength={0}
        sx={{ width: '50ch' }}
        label="Select a membership status"
        preLoadedOptions={MEMBERSHIP_STATUSES_WITH_IDS}
        value={value?.statuses ?? []}
        onChange={handleChangeStatuses}
        loadingText="Looking for membership statuses..."
        noOptionsText="No membership statuses found"
        boxSx={{ width: '50ch' }}
        templateBuilderDisplayName="Membership status filter: Status"
        isOnTemplatePage={inSearchTemplate}
      />
      {showBranchesAutocomplete && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            alignSelf: 'start',
            flexGrow: 1,
          }}
        >
          <span style={missingFilterValueWarningStyle}>
            <MissingFilterValueWarning isMissing={(value?.errors.length ?? 0) > 0} />
          </span>
          <Grid>
            <TemplateCapableFetchAutocomplete
              minLength={3}
              sx={{ width: '50ch' }}
              lookup={lookupBranches}
              label="Select a branch (optional)"
              value={value?.branches ?? []}
              onChange={handleChangeBranches}
              data-testid="branches"
              loadingText="Looking for branches statuses..."
              noOptionsText="No branches found"
              popupIcon={<BranchesPopupIcon />}
              error={(value?.errors.length ?? 0) > 0}
              disableIconFlip
              isOnTemplatePage={inSearchTemplate}
              templateBuilderDisplayName="Membership status filter: Branch"
            />

            <Grid container spacing={2} sx={{ marginTop: '8px' }}>
              <Grid
                size={6}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Tooltip title='Set to "Exclude" to omit people who do not intend to renew'>
                  <InputLabel id="membership-notRenewing-label" sx={{ color: 'black' }}>
                    Not Renewing (optional)
                  </InputLabel>
                </Tooltip>
              </Grid>
              <Grid size={6}>
                <FormControl fullWidth focused={isNotRenewingPlaceHolder}>
                  <TemplateCapableSelect
                    data-testid="notRenewing"
                    isOnTemplatePage={inSearchTemplate}
                    labelId={'membership-notRenewing-label'}
                    value={value?.notRenewing || listValuesForDisplay[0].value}
                    onChange={handleChangeNotRenewing}
                    valueAfterRemovePlaceholder={listValuesForDisplay[0].value}
                    templateBuilderDisplayName={'Membership status filter: Not renewing'}
                  >
                    {listValuesForDisplay.map((x) => (
                      <MenuItem key={x.value} value={x.value}>
                        {x.title}
                      </MenuItem>
                    ))}
                  </TemplateCapableSelect>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export const membershipWidget: BaseWidget & MembershipWidgetProps = {
  type: 'membership',
  hideOperator: true,
  operatorInlineLabel: ' is one of',
  factory: MembershipWidgetFactory,
  domainCode: '',

  // Wasn't sure on best way to handle required props for a factory...
  lookupBranches: () => {
    throw new Error('lookupBranches prop must be specified');
  },
};
